<template>
  <header class="bg-blue-primary flex flex-col items-center justify-center">
    <LazyLoginForm
      v-if="i18nLocale === 'en' && showLoginForm"
      class="w-full"
      @close="showLoginForm = false" />
    <div
      class="bg-blue-primary flex items-center z-40 px-ds-md md:px-ds-lg lg:px-ds-xxl xl:px-ds-xxxl rebrandNav:px-ds-xxxl rebrandLg:px-0 h-[65px] w-full rebrandLg:max-w-[1320px]"
    >
      <AnyVanLogo class="mr-ds-md lg:mr-ds-lg" qa-tag="nav-logo" @click="track('logo', 'logo')" />

      <LazyNavOurServicesEn
        v-if="i18nLocale === 'en'"
        ref="ourServicesMenu"
        qa-tag="nav-our-services"
        @show-login-form="showLoginForm = true"
        @show-help-centre="toggleHelpCentre(true)"
        @hide-other-menus="hideHelpCentreDesktop()"
        @track="track"
      />
      <LazyNavHelpCentreEn
        v-if="i18nLocale === 'en'"
        ref="helpCentreMenu"
        class="mx-ds-sm"
        qa-tag="nav-help-centre"
        @hide="toggleOurServices(false)"
        @hide-other-menus="hideOurServicesDesktop()"
        @track="track"
      />

      <LazyNavOurServicesUs
        v-if="i18nLocale === 'us'"
        ref="ourServicesMenu"
        qa-tag="nav-our-services"
        @show-login-form="showLoginForm = true"
        @show-help-centre="toggleHelpCentre(true)"
        @hide-other-menus="hideHelpCentreDesktop()"
        @track="track"
      />
      <NavButton
        v-if="i18nLocale === 'us'"
        class="mx-ds-sm bg-transparent hidden md:flex">
        <a
          class="text-inherit hover:text-inherit hover:no-underline focus:text-inherit focus:no-underline"
          href="/us/become-an-anyvan-shipping-partner"
        >
          Become a shipping partner
        </a>
      </NavButton>

      <LazyNavOurServicesEs
        v-if="i18nLocale === 'es'"
        ref="ourServicesMenu"
        qa-tag="nav-our-services"
        @show-login-form="showLoginForm = true"
        @show-help-centre="toggleHelpCentre(true)"
        @hide-other-menus="hideHelpCentreDesktop()"
        @track="track"
      />

      <LazyNavOurServicesFr
        v-if="i18nLocale === 'fr'"
        ref="ourServicesMenu"
        qa-tag="nav-our-services"
        @show-login-form="showLoginForm = true"
        @show-help-centre="toggleHelpCentre(true)"
        @hide-other-menus="hideHelpCentreDesktop()"
        @track="track"
      />
      <NavButton
        v-if="i18nLocale === 'fr'"
        class="mx-ds-sm bg-transparent hidden md:flex">
        <a
          class="text-inherit hover:text-inherit hover:no-underline focus:text-inherit focus:no-underline"
          href="/fr/devenir-transporteur-anyvan"
        >
          Devenir transporteur
        </a>
      </NavButton>

      <LazyNavOurServicesDe
        v-if="i18nLocale === 'de'"
        ref="ourServicesMenu"
        qa-tag="nav-our-services"
        @show-login-form="showLoginForm = true"
        @show-help-centre="toggleHelpCentre(true)"
        @hide-other-menus="hideHelpCentreDesktop()"
        @track="track"
      />

      <div
        class="flex-grow h-full hidden md:block"
        @click="
          () => {
            hideOurServicesDesktop()
            hideHelpCentreDesktop()
          }
        "
      />
      <NavCallUs
        qa-tag="nav-call-us"
        @hide-other-menus="
          () => {
            hideOurServicesDesktop()
            hideHelpCentreDesktop()
          }
        "
      />
      <NavLoginButton
        header
        qa-tag="nav-login-button"
        class="hidden md:flex"
        :redirect-url="loginButtonRedirectUrl"
        @show-login-form="toggleLoginForm"
      />
      <div class="md:hidden ml-ds-lg" @click="toggleOurServices(true)">
        <Icon
          qa-tag="nav-burger"
          class="text-white"
          :size="24"
          name="i:burger" />
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { getLoginPage, getScreenWidth, getV4Host } from "~/scripts/useful-functions"
import type NavOurServicesBase from "~/components/base/navOurServices"
import type NavHelpCentreBase from "~/components/base/navHelpCentre"
import { LOCALE } from "~/constants/locale"

interface ComponentRefs {
  ourServicesMenu: InstanceType<typeof NavOurServicesBase>
  helpCentreMenu: InstanceType<typeof NavHelpCentreBase>
}

export default defineComponent({
  name: "NavHeader",
  setup() {
    const i18n = useI18n()
    return { i18nLocale: i18n.locale.value }
  },
  data() {
    return {
      showLoginForm: false
    }
  },
  computed: {
    loginButtonRedirectUrl(): string | undefined {
      if (this.i18nLocale == 'en') {
        return undefined
      }
      return getV4Host(this.$config.public.v4Host, this.i18nLocale) + "/" + getLoginPage(this.i18nLocale)
    }
  },
  methods: {
    toggleOurServices(override: null | boolean = null) {
      const ourServicesMenu = this.$refs
        .ourServicesMenu as unknown as ComponentRefs["ourServicesMenu"]
      if (ourServicesMenu) {
        ourServicesMenu.toggleMenu(override)
      }
    },
    toggleHelpCentre(override: null | boolean = null) {
      const helpCentreMenu = this.$refs.helpCentreMenu as unknown as ComponentRefs["helpCentreMenu"]
      if (helpCentreMenu) {
        helpCentreMenu.toggleMenu(override)
      }
    },
    hideOurServicesDesktop() {
      const screenWidth = getScreenWidth()
      if (screenWidth && screenWidth >= 768) {
        this.toggleOurServices(false)
      }
    },
    hideHelpCentreDesktop() {
      const screenWidth = getScreenWidth()
      if (screenWidth && screenWidth >= 768) {
        this.toggleHelpCentre(false)
      }
    },
    track(property: string, label: string) {
      this.$snowplow.trackNavMenuClickEvent(property, label)
    },
    toggleLoginForm() {
      const supportedLocales = [LOCALE.UK]

      if (!supportedLocales.includes(this.i18nLocale)) {
        return
      }

      this.hideOurServicesDesktop()
      this.hideHelpCentreDesktop()
      this.showLoginForm = true
    },

}

})
</script>
